import React, { useState, useCallback, useMemo, memo, useEffect, ReactNode } from 'react';

// Type definitions
interface HelmetProps {
  children: React.ReactElement;
}

interface AnalyticsEvent {
  event: string;
  properties: Record<string, any>;
  timestamp: string;
}

interface ConversionOptions {
  outputFormat: OutputFormat;
  extractionMode: ExtractionMode;
  pageRange: string;
  includeImages: boolean;
  includeTextBoxes: boolean;
  preserveFormatting: boolean;
  includeMetadata: boolean;
  includeStructure: boolean;
  textExtraction: TextExtractionMode;
  imageHandling: ImageHandling;
  qualityLevel: QualityLevel;
  ocrEnabled: boolean;
  ocrLanguage: string;
  removeWatermarks: boolean;
  rotationAngle: number;
  contrast: number;
  brightness: number;
  cleanupText: boolean;
  xmlSchema: XMLSchema;
  rootElement: string;
  namespaceUri: string;
  includeAnnotations: boolean;
  includeForms: boolean;
  includeBookmarks: boolean;
  encoding: string;
  indentation: boolean;
  validateXML: boolean;
  xsltTransform: string;
  compressionLevel: CompressionLevel;
}

interface ThemeContextType {
  isDark: boolean;
  toggleTheme: () => void;
}

interface AnalyticsHook {
  track: (event: string, properties?: Record<string, any>) => void;
}

interface SEOData {
  title: string;
  description: string;
  keywords: string;
  canonical: string;
  ogTitle: string;
  ogDescription: string;
  ogImage: string;
  twitterCard: string;
}

interface AnalyticsStats {
  total: number;
  conversions: number;
  validations: number;
}

interface PDFInfo {
  fileName: string;
  fileSize: number;
  pageCount: number;
  version: string;
  hasImages: boolean;
  hasTables: boolean;
  hasAnnotations: boolean;
  hasForms: boolean;
  hasBookmarks: boolean;
  isPasswordProtected: boolean;
  metadata: {
    title?: string;
    author?: string;
    subject?: string;
    creator?: string;
    producer?: string;
    creationDate?: string;
    modificationDate?: string;
  };
}

// Output format options
type OutputFormat = 'xml' | 'json' | 'html' | 'txt' | 'csv';
type ExtractionMode = 'full-document' | 'text-only' | 'structure-only' | 'tables-only' | 'images-only' | 'metadata-only';
type TextExtractionMode = 'layout-preserved' | 'reading-order' | 'raw-text' | 'structured-text';
type ImageHandling = 'ignore' | 'embed' | 'extract' | 'link' | 'base64';
type QualityLevel = 'fast' | 'balanced' | 'high' | 'ultra';
type XMLSchema = 'custom' | 'tei' | 'dublin-core' | 'mods' | 'alto' | 'page-xml';
type CompressionLevel = 'none' | 'low' | 'medium' | 'high' | 'maximum';

// Mock react-helmet-async for SEO
const Helmet: React.FC<HelmetProps> = ({ children }) => {
  useEffect(() => {
    const extractPropsFromChildren = (element: React.ReactElement): any => {
      if (element.props?.children) {
        const childArray = React.Children.toArray(element.props.children);
        const helmetData: any = { meta: [], link: [] };

        childArray.forEach((child: any) => {
          if (React.isValidElement(child)) {
            const childElement = child as React.ReactElement<any>;

            if (childElement.type === 'title') {
              helmetData.title = childElement.props.children;
            } else if (childElement.type === 'meta') {
              helmetData.meta.push(childElement.props);
            } else if (childElement.type === 'link') {
              helmetData.link.push(childElement.props);
            } else if (childElement.type === 'script') {
              if (childElement.props.type === 'application/ld+json') {
                const script = document.createElement('script');
                script.type = 'application/ld+json';
                script.textContent = childElement.props.children;
                document.head.appendChild(script);
              }
            }
          }
        });
        return helmetData;
      }
      return {};
    };

    const helmetData = extractPropsFromChildren(children);

    if (helmetData.title) {
      document.title = helmetData.title;
    }

    const updateMeta = (name: string, content: string): void => {
      let meta = document.querySelector(`meta[name="${name}"]`) ||
        document.querySelector(`meta[property="${name}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        if (name.startsWith('og:') || name.startsWith('twitter:')) {
          meta.setAttribute('property', name);
        } else {
          meta.setAttribute('name', name);
        }
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };

    if (helmetData.meta && Array.isArray(helmetData.meta)) {
      helmetData.meta.forEach((meta: any) => {
        if (meta.name) updateMeta(meta.name, meta.content);
        if (meta.property) updateMeta(meta.property, meta.content);
      });
    }

    if (helmetData.link && Array.isArray(helmetData.link)) {
      helmetData.link.forEach((link: any) => {
        let linkEl = document.querySelector(`link[rel="${link.rel}"]`) as HTMLLinkElement;
        if (!linkEl) {
          linkEl = document.createElement('link') as HTMLLinkElement;
          linkEl.rel = link.rel;
          document.head.appendChild(linkEl);
        }
        Object.keys(link).forEach((attr) => {
          if (attr !== 'rel') linkEl.setAttribute(attr, link[attr]);
        });
      });
    }
  }, [children]);

  return null;
};

// Analytics hook with in-memory storage
const useAnalytics = (): AnalyticsHook => {
  const [analyticsData, setAnalyticsData] = useState<AnalyticsEvent[]>([]);

  const track = useCallback((event: string, properties: Record<string, any> = {}): void => {
    console.log('Analytics Event:', event, properties);

    const analyticsEvent: AnalyticsEvent = {
      event,
      properties,
      timestamp: new Date().toISOString()
    };

    setAnalyticsData(prev => [...prev.slice(-99), analyticsEvent]);

    // Also track with gtag if available
    if (typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('event', event, properties);
    }
  }, []);

  return { track };
};

// Theme context
const ThemeContext = React.createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
  children: ReactNode;
}

const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [isDark, setIsDark] = useState<boolean>(() => {
    if (typeof window !== 'undefined') {
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      return prefersDark;
    }
    return false;
  });

  const toggleTheme = useCallback((): void => {
    setIsDark((prev) => !prev);
  }, []);

  useEffect(() => {
    document.documentElement.classList.toggle('dark', isDark);
  }, [isDark]);

  return (
    <ThemeContext.Provider value={{ isDark, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

const useTheme = (): ThemeContextType => {
  const context = React.useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

// Mock PDF analysis function
const analyzePDFFile = async (file: File): Promise<PDFInfo> => {
  // Simulate PDF analysis
  await new Promise(resolve => setTimeout(resolve, 1000));
  
  return {
    fileName: file.name,
    fileSize: file.size,
    pageCount: Math.floor(Math.random() * 50) + 1,
    version: '1.4',
    hasImages: Math.random() > 0.5,
    hasTables: Math.random() > 0.3,
    hasAnnotations: Math.random() > 0.6,
    hasForms: Math.random() > 0.7,
    hasBookmarks: Math.random() > 0.4,
    isPasswordProtected: Math.random() > 0.8,
    metadata: {
      title: 'Sample PDF Document',
      author: 'Document Author',
      subject: 'PDF Analysis',
      creator: 'PDF Creator',
      producer: 'PDF Producer',
      creationDate: '2024-01-01',
      modificationDate: new Date().toISOString().split('T')[0]
    }
  };
};

// Mock PDF to XML conversion
const convertPDFToXML = async (file: File, options: ConversionOptions): Promise<Blob> => {
  // Simulate conversion process
  await new Promise(resolve => setTimeout(resolve, 3000));
  
  // Create mock XML data based on selected schema and options
  let mockXMLData = '';
  
  if (options.xmlSchema === 'custom') {
    mockXMLData = `<?xml version="1.0" encoding="${options.encoding}"?>
<${options.rootElement || 'document'}${options.namespaceUri ? ` xmlns="${options.namespaceUri}"` : ''}>
  <metadata>
    <title>Sample PDF Document</title>
    <author>Document Author</author>
    <creation-date>2024-01-01</creation-date>
    <page-count>5</page-count>
  </metadata>
  <content>
    <page number="1">
      <text-block x="72" y="720" width="468" height="24">
        <text>Sample Document Title</text>
      </text-block>
      <text-block x="72" y="680" width="468" height="120">
        <text>This is sample content extracted from the PDF document. The text maintains its original structure and formatting information.</text>
      </text-block>
      ${options.includeImages ? '<image x="72" y="400" width="200" height="150" src="image1.jpg" />' : ''}
    </page>
    <page number="2">
      <table x="72" y="600" width="468" height="200">
        <row>
          <cell>Name</cell>
          <cell>Age</cell>
          <cell>Department</cell>
        </row>
        <row>
          <cell>John Doe</cell>
          <cell>30</cell>
          <cell>Engineering</cell>
        </row>
      </table>
    </page>
  </content>
</${options.rootElement || 'document'}>`;
  } else if (options.xmlSchema === 'tei') {
    mockXMLData = `<?xml version="1.0" encoding="${options.encoding}"?>
<TEI xmlns="http://www.tei-c.org/ns/1.0">
  <teiHeader>
    <fileDesc>
      <titleStmt>
        <title>Sample PDF Document</title>
        <author>Document Author</author>
      </titleStmt>
      <publicationStmt>
        <p>Converted from PDF</p>
      </publicationStmt>
      <sourceDesc>
        <p>Original PDF document</p>
      </sourceDesc>
    </fileDesc>
  </teiHeader>
  <text>
    <body>
      <div type="page" n="1">
        <head>Sample Document Title</head>
        <p>This is sample content extracted from the PDF document.</p>
      </div>
    </body>
  </text>
</TEI>`;
  } else if (options.xmlSchema === 'dublin-core') {
    mockXMLData = `<?xml version="1.0" encoding="${options.encoding}"?>
<metadata xmlns:dc="http://purl.org/dc/elements/1.1/">
  <dc:title>Sample PDF Document</dc:title>
  <dc:creator>Document Author</dc:creator>
  <dc:subject>PDF Analysis</dc:subject>
  <dc:description>Content extracted from PDF document</dc:description>
  <dc:publisher>PDF Converter</dc:publisher>
  <dc:date>2024-01-01</dc:date>
  <dc:type>Text</dc:type>
  <dc:format>application/pdf</dc:format>
  <dc:language>en</dc:language>
</metadata>`;
  }

  const mimeType = options.outputFormat === 'json' ? 'application/json' :
                   options.outputFormat === 'html' ? 'text/html' :
                   options.outputFormat === 'txt' ? 'text/plain' :
                   options.outputFormat === 'csv' ? 'text/csv' :
                   'application/xml';

  return new Blob([mockXMLData], { type: mimeType });
};

// Analytics Dashboard Component
const AnalyticsDashboard: React.FC = memo(() => {
  const [analytics, setAnalytics] = useState<AnalyticsEvent[]>([]);
  const { isDark } = useTheme();

  const stats: AnalyticsStats = useMemo(() => {
    const total = analytics.length;
    const conversions = analytics.filter((a) => a.event === 'pdf_converted').length;
    const validations = analytics.filter((a) => a.event === 'pdf_analyzed').length;

    return { total, conversions, validations };
  }, [analytics]);

  return (
    <div className={`p-4 rounded-lg border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
      <h3 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
        Usage Analytics
      </h3>
      <div className="grid grid-cols-4 gap-4">
        <div className={`text-center p-3 rounded ${isDark ? 'bg-gray-700' : 'bg-white'}`}>
          <div className={`text-2xl font-bold ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>
            {Math.floor(Math.random() * 100) + 50}
          </div>
          <div className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
            Total Files
          </div>
        </div>
        <div className={`text-center p-3 rounded ${isDark ? 'bg-gray-700' : 'bg-white'}`}>
          <div className={`text-2xl font-bold ${isDark ? 'text-green-400' : 'text-green-600'}`}>
            {Math.floor(Math.random() * 50) + 20}
          </div>
          <div className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
            Conversions
          </div>
        </div>
        <div className={`text-center p-3 rounded ${isDark ? 'bg-gray-700' : 'bg-white'}`}>
          <div className={`text-2xl font-bold ${isDark ? 'text-purple-400' : 'text-purple-600'}`}>
            {Math.floor(Math.random() * 30) + 10}
          </div>
          <div className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
            Success Rate
          </div>
        </div>
        <div className={`text-center p-3 rounded ${isDark ? 'bg-gray-700' : 'bg-white'}`}>
          <div className={`text-2xl font-bold ${isDark ? 'text-orange-400' : 'text-orange-600'}`}>
            {Math.floor(Math.random() * 1000) + 500}MB
          </div>
          <div className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
            Data Processed
          </div>
        </div>
      </div>
    </div>
  );
});

AnalyticsDashboard.displayName = 'AnalyticsDashboard';

// PDF Preview Component
const PDFPreview: React.FC<{ pdfInfo: PDFInfo | null; isDark: boolean }> = memo(({ pdfInfo, isDark }) => {
  if (!pdfInfo) return null;

  return (
    <div className={`p-4 rounded-lg border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
      <h3 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
        PDF Analysis
      </h3>
      
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <span className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>File Name:</span>
            <p className={`${isDark ? 'text-white' : 'text-gray-900'}`}>{pdfInfo.fileName}</p>
          </div>
          <div>
            <span className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>File Size:</span>
            <p className={`${isDark ? 'text-white' : 'text-gray-900'}`}>
              {(pdfInfo.fileSize / 1024 / 1024).toFixed(2)} MB
            </p>
          </div>
        </div>
        
        <div className="grid grid-cols-2 gap-4">
          <div>
            <span className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>Pages:</span>
            <p className={`${isDark ? 'text-white' : 'text-gray-900'}`}>{pdfInfo.pageCount}</p>
          </div>
          <div>
            <span className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>PDF Version:</span>
            <p className={`${isDark ? 'text-white' : 'text-gray-900'}`}>{pdfInfo.version}</p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="flex items-center">
            <div className={`w-3 h-3 rounded-full mr-2 ${pdfInfo.hasTables ? 'bg-green-500' : 'bg-red-500'}`}></div>
            <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
              {pdfInfo.hasTables ? 'Has Tables' : 'No Tables'}
            </span>
          </div>
          <div className="flex items-center">
            <div className={`w-3 h-3 rounded-full mr-2 ${pdfInfo.hasImages ? 'bg-green-500' : 'bg-red-500'}`}></div>
            <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
              {pdfInfo.hasImages ? 'Has Images' : 'No Images'}
            </span>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-2">
          <div className="flex items-center">
            <div className={`w-3 h-3 rounded-full mr-2 ${pdfInfo.hasAnnotations ? 'bg-green-500' : 'bg-red-500'}`}></div>
            <span className={`text-xs ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
              {pdfInfo.hasAnnotations ? 'Annotations' : 'No Annotations'}
            </span>
          </div>
          <div className="flex items-center">
            <div className={`w-3 h-3 rounded-full mr-2 ${pdfInfo.hasForms ? 'bg-green-500' : 'bg-red-500'}`}></div>
            <span className={`text-xs ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
              {pdfInfo.hasForms ? 'Forms' : 'No Forms'}
            </span>
          </div>
          <div className="flex items-center">
            <div className={`w-3 h-3 rounded-full mr-2 ${pdfInfo.hasBookmarks ? 'bg-green-500' : 'bg-red-500'}`}></div>
            <span className={`text-xs ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
              {pdfInfo.hasBookmarks ? 'Bookmarks' : 'No Bookmarks'}
            </span>
          </div>
        </div>

        {pdfInfo.metadata && (
          <div className={`mt-4 p-3 rounded ${isDark ? 'bg-gray-700' : 'bg-white'}`}>
            <h4 className={`text-sm font-medium mb-2 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>Metadata</h4>
            <div className="space-y-1 text-sm">
              {pdfInfo.metadata.title && (
                <div>
                  <span className={`font-medium ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>Title: </span>
                  <span className={`${isDark ? 'text-white' : 'text-gray-900'}`}>{pdfInfo.metadata.title}</span>
                </div>
              )}
              {pdfInfo.metadata.author && (
                <div>
                  <span className={`font-medium ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>Author: </span>
                  <span className={`${isDark ? 'text-white' : 'text-gray-900'}`}>{pdfInfo.metadata.author}</span>
                </div>
              )}
              {pdfInfo.metadata.creationDate && (
                <div>
                  <span className={`font-medium ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>Created: </span>
                  <span className={`${isDark ? 'text-white' : 'text-gray-900'}`}>{pdfInfo.metadata.creationDate}</span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
});

PDFPreview.displayName = 'PDFPreview';

// Main Component Content
const PDFToXMLContent: React.FC = () => {
  const { isDark, toggleTheme } = useTheme();
  const { track } = useAnalytics();

  // States
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [pdfInfo, setPdfInfo] = useState<PDFInfo | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState<boolean>(false);
  const [isConverting, setIsConverting] = useState<boolean>(false);
  const [conversionProgress, setConversionProgress] = useState<number>(0);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [fileName, setFileName] = useState<string>('converted_data');
  const [showAdvanced, setShowAdvanced] = useState<boolean>(false);
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
  const [showAnalytics, setShowAnalytics] = useState<boolean>(false);
  const [dragActive, setDragActive] = useState<boolean>(false);

  // Advanced options
  const [outputFormat, setOutputFormat] = useState<OutputFormat>('xml');
  const [extractionMode, setExtractionMode] = useState<ExtractionMode>('full-document');
  const [pageRange, setPageRange] = useState<string>('all');
  const [includeImages, setIncludeImages] = useState<boolean>(false);
  const [includeTextBoxes, setIncludeTextBoxes] = useState<boolean>(true);
  const [preserveFormatting, setPreserveFormatting] = useState<boolean>(true);
  const [includeMetadata, setIncludeMetadata] = useState<boolean>(true);
  const [includeStructure, setIncludeStructure] = useState<boolean>(true);
  const [textExtraction, setTextExtraction] = useState<TextExtractionMode>('layout-preserved');
  const [imageHandling, setImageHandling] = useState<ImageHandling>('link');
  const [qualityLevel, setQualityLevel] = useState<QualityLevel>('balanced');
  const [ocrEnabled, setOcrEnabled] = useState<boolean>(false);
  const [ocrLanguage, setOcrLanguage] = useState<string>('eng');
  const [removeWatermarks, setRemoveWatermarks] = useState<boolean>(false);
  const [rotationAngle, setRotationAngle] = useState<number>(0);
  const [contrast, setContrast] = useState<number>(100);
  const [brightness, setBrightness] = useState<number>(100);
  const [cleanupText, setCleanupText] = useState<boolean>(true);
  const [xmlSchema, setXmlSchema] = useState<XMLSchema>('custom');
  const [rootElement, setRootElement] = useState<string>('document');
  const [namespaceUri, setNamespaceUri] = useState<string>('');
  const [includeAnnotations, setIncludeAnnotations] = useState<boolean>(true);
  const [includeForms, setIncludeForms] = useState<boolean>(true);
  const [includeBookmarks, setIncludeBookmarks] = useState<boolean>(true);
  const [encoding, setEncoding] = useState<string>('UTF-8');
  const [indentation, setIndentation] = useState<boolean>(true);
  const [validateXML, setValidateXML] = useState<boolean>(true);
  const [xsltTransform, setXsltTransform] = useState<string>('');
  const [compressionLevel, setCompressionLevel] = useState<CompressionLevel>('medium');

  // SEO data
  const seoData: SEOData = useMemo(() => ({
    title: "Free PDF to XML Converter Online - Extract Structured Data from PDF Files",
    description: "Convert PDF documents to XML format online for free. Advanced features include schema support, metadata extraction, structure preservation, and OCR. Perfect for data processing and document analysis.",
    keywords: "PDF to XML converter, PDF converter, XML converter, online converter, free PDF converter, PDF parser, XML formatter, data extraction, structure extraction",
    canonical: "https://yoursite.com/pdf-to-xml-converter",
    ogTitle: "PDF to XML Converter - Free Online Tool",
    ogDescription: "Convert your PDF files to XML format instantly. Supports multiple schemas, metadata extraction, and advanced formatting options for perfect data extraction.",
    ogImage: "https://yoursite.com/og-pdf-xml-converter.jpg",
    twitterCard: "summary_large_image"
  }), []);

  // Handle file selection
  const handleFileSelect = useCallback(async (file: File): Promise<void> => {
    if (!file.type.includes('pdf')) {
      setError('Please select a valid PDF file');
      return;
    }

    if (file.size > 100 * 1024 * 1024) { // 100MB limit
      setError('File size must be less than 100MB');
      return;
    }

    setSelectedFile(file);
    setError('');
    setSuccess('');
    setPdfInfo(null);
    
    // Set default filename
    const nameParts = file.name.split('.');
    if (nameParts.length > 1) {
      nameParts.pop();
      setFileName(nameParts.join('.'));
    } else {
      setFileName(file.name);
    }

    track('pdf_file_selected', {
      fileName: file.name,
      fileSize: file.size
    });

    // Analyze PDF
    setIsAnalyzing(true);
    try {
      const info = await analyzePDFFile(file);
      setPdfInfo(info);
      track('pdf_analyzed', {
        pageCount: info.pageCount,
        hasImages: info.hasImages,
        hasTables: info.hasTables,
        hasAnnotations: info.hasAnnotations,
        hasForms: info.hasForms,
        hasBookmarks: info.hasBookmarks,
        isPasswordProtected: info.isPasswordProtected
      });
    } catch (err) {
      setError(`Failed to analyze PDF: ${err instanceof Error ? err.message : String(err)}`);
      track('pdf_analysis_error', { error: err instanceof Error ? err.message : String(err) });
    } finally {
      setIsAnalyzing(false);
    }
  }, [track]);

  // Handle file upload
  const handleFileUpload = useCallback((e: React.ChangeEvent<HTMLInputElement>): void => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  }, [handleFileSelect]);

  // Handle drag and drop
  const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    setDragActive(false);
    
    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  }, [handleFileSelect]);

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    setDragActive(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    setDragActive(false);
  }, []);

  // Convert PDF to XML
  const convertToXML = useCallback(async (): Promise<void> => {
    if (!selectedFile) {
      setError('Please select a PDF file first');
      return;
    }

    setError('');
    setSuccess('');
    setIsConverting(true);
    setConversionProgress(0);

    const options: ConversionOptions = {
      outputFormat,
      extractionMode,
      pageRange,
      includeImages,
      includeTextBoxes,
      preserveFormatting,
      includeMetadata,
      includeStructure,
      textExtraction,
      imageHandling,
      qualityLevel,
      ocrEnabled,
      ocrLanguage,
      removeWatermarks,
      rotationAngle,
      contrast,
      brightness,
      cleanupText,
      xmlSchema,
      rootElement,
      namespaceUri,
      includeAnnotations,
      includeForms,
      includeBookmarks,
      encoding,
      indentation,
      validateXML,
      xsltTransform,
      compressionLevel
    };

    try {
      const startTime = Date.now();

      // Simulate progress updates
      const progressInterval = setInterval(() => {
        setConversionProgress(prev => {
          if (prev >= 90) return prev;
          return prev + Math.random() * 10;
        });
      }, 200);

      const resultBlob = await convertPDFToXML(selectedFile, options);
      
      clearInterval(progressInterval);
      setConversionProgress(100);

      // Download the result
      const url = URL.createObjectURL(resultBlob);
      const link = document.createElement('a');
      link.href = url;
      const extension = outputFormat === 'xml' ? 'xml' :
                      outputFormat === 'json' ? 'json' :
                      outputFormat === 'html' ? 'html' :
                      outputFormat === 'txt' ? 'txt' : 'csv';
      link.download = `${fileName || 'converted_data'}.${extension}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      const processingTime = Date.now() - startTime;
      setSuccess(`PDF successfully converted to ${outputFormat.toUpperCase()}! Download started automatically.`);
      
      track('pdf_converted', {
        format: outputFormat,
        schema: xmlSchema,
        processingTime,
        fileSize: selectedFile.size,
        pageCount: pdfInfo?.pageCount,
        options: {
          extractionMode,
          qualityLevel,
          ocrEnabled,
          includeImages,
          includeMetadata,
          includeStructure
        }
      });

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : String(err);
      setError(`Conversion failed: ${errorMessage}`);
      track('conversion_error', { error: errorMessage });
    } finally {
      setIsConverting(false);
      setConversionProgress(0);
    }
  }, [selectedFile, fileName, outputFormat, extractionMode, pageRange, includeImages, includeTextBoxes, preserveFormatting, includeMetadata, includeStructure, textExtraction, imageHandling, qualityLevel, ocrEnabled, ocrLanguage, removeWatermarks, rotationAngle, contrast, brightness, cleanupText, xmlSchema, rootElement, namespaceUri, includeAnnotations, includeForms, includeBookmarks, encoding, indentation, validateXML, xsltTransform, compressionLevel, pdfInfo?.pageCount, track]);

  // Clear all
  const clearAll = useCallback((): void => {
    setSelectedFile(null);
    setPdfInfo(null);
    setError('');
    setSuccess('');
    setFileName('converted_data');
    setConversionProgress(0);
    track('data_cleared');
  }, [track]);

  // Toggle fullscreen
  const toggleFullscreen = useCallback((): void => {
    setIsFullscreen(!isFullscreen);
    track('fullscreen_toggled', { isFullscreen: !isFullscreen });
  }, [isFullscreen, track]);

  return (
    <div className={`min-h-screen transition-colors duration-200 ${isDark ? 'bg-gray-900' : 'bg-gray-50'} ${isFullscreen ? 'fixed inset-0 z-50 overflow-auto' : ''}`}>

      {/* SEO Meta Tags */}
      <Helmet>
        <>
          <title>{seoData.title}</title>
          <meta name="description" content={seoData.description} />
          <meta name="keywords" content={seoData.keywords} />
          <link rel="canonical" href={seoData.canonical} />

          <meta property="og:title" content={seoData.ogTitle} />
          <meta property="og:description" content={seoData.ogDescription} />
          <meta property="og:image" content={seoData.ogImage} />
          <meta property="og:type" content="website" />
          <meta property="og:url" content={seoData.canonical} />

          <meta name="twitter:card" content={seoData.twitterCard} />
          <meta name="twitter:title" content={seoData.ogTitle} />
          <meta name="twitter:description" content={seoData.ogDescription} />
          <meta name="twitter:image" content={seoData.ogImage} />

          <meta name="robots" content="index, follow" />
          <meta name="author" content="PDF to XML Converter Tool" />
          <meta name="viewport" content="width=device-width, initial-scale=1.0" />
          <meta httpEquiv="Content-Type" content="text/html; charset=utf-8" />

          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "WebApplication",
              "name": "PDF to XML Converter",
              "description": seoData.description,
              "url": seoData.canonical,
              "applicationCategory": "UtilityApplication",
              "operatingSystem": "Any",
              "offers": {
                "@type": "Offer",
                "price": "0",
                "priceCurrency": "USD"
              }
            })}
          </script>
        </>
      </Helmet>

      <div className={`${isFullscreen ? 'p-4' : 'max-w-7xl mx-auto p-6'}`}>

        {/* Header with Controls */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              PDF to XML Converter
            </h1>
            <p className={`mt-2 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
              Convert PDF files to XML format with advanced structure extraction, metadata preservation, and schema support
            </p>
          </div>

          <div className="flex space-x-2">
            {/* Analytics Toggle */}
            <button
              onClick={() => setShowAnalytics(!showAnalytics)}
              className={`p-2 rounded-lg transition-colors ${isDark ? 'bg-gray-800 hover:bg-gray-700 text-white' : 'bg-white hover:bg-gray-50 text-gray-900'} border ${isDark ? 'border-gray-700' : 'border-gray-200'}`}
              title="Toggle Analytics"
              type="button"
            >
              📊
            </button>

            {/* Theme Toggle */}
            <button
              onClick={toggleTheme}
              className={`p-2 rounded-lg transition-colors ${isDark ? 'bg-gray-800 hover:bg-gray-700 text-white' : 'bg-white hover:bg-gray-50 text-gray-900'} border ${isDark ? 'border-gray-700' : 'border-gray-200'}`}
              title="Toggle Theme"
              type="button"
            >
              {isDark ? '☀️' : '🌙'}
            </button>

            {/* Fullscreen Toggle */}
            <button
              onClick={toggleFullscreen}
              className={`p-2 rounded-lg transition-colors ${isDark ? 'bg-gray-800 hover:bg-gray-700 text-white' : 'bg-white hover:bg-gray-50 text-gray-900'} border ${isDark ? 'border-gray-700' : 'border-gray-200'}`}
              title="Toggle Fullscreen"
              type="button"
            >
              {isFullscreen ? '📱' : '🖥️'}
            </button>
          </div>
        </div>

        {/* Analytics Dashboard */}
        {showAnalytics && (
          <div className="mb-6">
            <AnalyticsDashboard />
          </div>
        )}

        {/* Description */}
        <div className={`mb-6 p-4 rounded-lg border ${isDark ? 'bg-blue-900/20 border-blue-800 text-blue-200' : 'bg-blue-50 border-blue-100 text-blue-800'}`}>
          <p>
            Convert PDF documents to structured XML format with intelligent content extraction, schema support, and metadata preservation.
            Perfect for data processing, document analysis, and content management systems.
          </p>
        </div>

        <div className={`grid ${isFullscreen ? 'grid-cols-1 lg:grid-cols-3' : 'grid-cols-1 md:grid-cols-3'} gap-6`}>

          {/* File Upload Section */}
          <div className="flex flex-col h-full">
            <div className={`p-4 rounded-lg shadow-sm mb-4 flex-grow border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
              <div className="flex justify-between items-center mb-3">
                <label className={`font-medium ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                  PDF File Upload
                </label>
                {selectedFile && (
                  <button
                    className={`text-sm transition-colors ${isDark ? 'text-gray-400 hover:text-gray-300' : 'text-gray-600 hover:text-gray-800'}`}
                    onClick={clearAll}
                    type="button"
                  >
                    Clear
                  </button>
                )}
              </div>

              {/* Drag and Drop Area */}
              <div
                className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                  dragActive
                    ? isDark ? 'border-blue-400 bg-blue-900/20' : 'border-blue-400 bg-blue-50'
                    : isDark ? 'border-gray-600 hover:border-gray-500' : 'border-gray-300 hover:border-gray-400'
                }`}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
              >
                <div className="space-y-4">
                  <div className={`text-4xl ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    📄
                  </div>
                  <div>
                    <p className={`text-lg font-medium ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                      Drop your PDF file here
                    </p>
                    <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                      or click to browse
                    </p>
                  </div>
                  <input
                    type="file"
                    accept=".pdf"
                    onChange={handleFileUpload}
                    className="hidden"
                    id="pdf-upload"
                  />
                  <label
                    htmlFor="pdf-upload"
                    className={`inline-block px-4 py-2 rounded-lg font-medium cursor-pointer transition-colors ${
                      isDark
                        ? 'bg-blue-600 hover:bg-blue-700 text-white'
                        : 'bg-blue-600 hover:bg-blue-700 text-white'
                    }`}
                  >
                    Select PDF File
                  </label>
                  <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                    Maximum file size: 100MB
                  </p>
                </div>
              </div>

              {/* File Info */}
              {selectedFile && (
                <div className={`mt-4 p-3 rounded-lg ${isDark ? 'bg-gray-700' : 'bg-gray-50'}`}>
                  <h4 className={`font-medium mb-2 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>Selected File</h4>
                  <div className="space-y-1 text-sm">
                    <div>
                      <span className={`font-medium ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>Name: </span>
                      <span className={`${isDark ? 'text-white' : 'text-gray-900'}`}>{selectedFile.name}</span>
                    </div>
                    <div>
                      <span className={`font-medium ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>Size: </span>
                      <span className={`${isDark ? 'text-white' : 'text-gray-900'}`}>
                        {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                      </span>
                    </div>
                    <div>
                      <span className={`font-medium ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>Type: </span>
                      <span className={`${isDark ? 'text-white' : 'text-gray-900'}`}>{selectedFile.type}</span>
                    </div>
                  </div>
                  
                  {isAnalyzing && (
                    <div className="mt-3 flex items-center">
                      <svg className="animate-spin h-4 w-4 mr-2 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>Analyzing PDF...</span>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* PDF Preview */}
            {pdfInfo && <PDFPreview pdfInfo={pdfInfo} isDark={isDark} />}
          </div>

          {/* Configuration Section */}
          <div className={`p-4 rounded-lg shadow-sm border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
            <h3 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Conversion Settings
            </h3>

            <div className="space-y-4">
              {/* Basic Settings */}
              <div>
                <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                  Output File Name
                </label>
                <input
                  type="text"
                  className={`w-full p-2 border rounded-lg transition-colors ${isDark ? 'bg-gray-700 border-gray-600 text-white focus:ring-blue-500 focus:border-blue-500' : 'bg-white border-gray-300 text-gray-900 focus:ring-blue-500 focus:border-blue-500'}`}
                  value={fileName}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFileName(e.target.value)}
                  placeholder="Enter file name (without extension)"
                />
              </div>

              <div>
                <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                  Output Format
                </label>
                <select
                  className={`w-full p-2 border rounded-lg transition-colors ${isDark ? 'bg-gray-700 border-gray-600 text-white focus:ring-blue-500 focus:border-blue-500' : 'bg-white border-gray-300 text-gray-900 focus:ring-blue-500 focus:border-blue-500'}`}
                  value={outputFormat}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setOutputFormat(e.target.value as OutputFormat)}
                >
                  <option value="xml">XML (.xml)</option>
                  <option value="json">JSON (.json)</option>
                  <option value="html">HTML (.html)</option>
                  <option value="txt">Plain Text (.txt)</option>
                  <option value="csv">CSV (.csv)</option>
                </select>
              </div>

              <div>
                <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                  Extraction Mode
                </label>
                <select
                  className={`w-full p-2 border rounded-lg transition-colors ${isDark ? 'bg-gray-700 border-gray-600 text-white focus:ring-blue-500 focus:border-blue-500' : 'bg-white border-gray-300 text-gray-900 focus:ring-blue-500 focus:border-blue-500'}`}
                  value={extractionMode}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setExtractionMode(e.target.value as ExtractionMode)}
                >
                  <option value="full-document">Full Document</option>
                  <option value="text-only">Text Only</option>
                  <option value="structure-only">Structure Only</option>
                  <option value="tables-only">Tables Only</option>
                  <option value="images-only">Images Only</option>
                  <option value="metadata-only">Metadata Only</option>
                </select>
              </div>

              {outputFormat === 'xml' && (
                <div>
                  <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                    XML Schema
                  </label>
                  <select
                    className={`w-full p-2 border rounded-lg transition-colors ${isDark ? 'bg-gray-700 border-gray-600 text-white focus:ring-blue-500 focus:border-blue-500' : 'bg-white border-gray-300 text-gray-900 focus:ring-blue-500 focus:border-blue-500'}`}
                    value={xmlSchema}
                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setXmlSchema(e.target.value as XMLSchema)}
                  >
                    <option value="custom">Custom Schema</option>
                    <option value="tei">TEI (Text Encoding Initiative)</option>
                    <option value="dublin-core">Dublin Core</option>
                    <option value="mods">MODS (Metadata Object Description Schema)</option>
                    <option value="alto">ALTO (Analyzed Layout and Text Object)</option>
                    <option value="page-xml">PAGE XML</option>
                  </select>
                </div>
              )}

              <div>
                <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                  Page Range
                </label>
                <input
                  type="text"
                  className={`w-full p-2 border rounded-lg transition-colors ${isDark ? 'bg-gray-700 border-gray-600 text-white focus:ring-blue-500 focus:border-blue-500' : 'bg-white border-gray-300 text-gray-900 focus:ring-blue-500 focus:border-blue-500'}`}
                  value={pageRange}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPageRange(e.target.value)}
                  placeholder="e.g., 1-5, 8, 10-12 or 'all'"
                />
              </div>

              <div>
                <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                  Quality Level
                </label>
                <select
                  className={`w-full p-2 border rounded-lg transition-colors ${isDark ? 'bg-gray-700 border-gray-600 text-white focus:ring-blue-500 focus:border-blue-500' : 'bg-white border-gray-300 text-gray-900 focus:ring-blue-500 focus:border-blue-500'}`}
                  value={qualityLevel}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setQualityLevel(e.target.value as QualityLevel)}
                >
                  <option value="fast">Fast (Quick processing)</option>
                  <option value="balanced">Balanced (Recommended)</option>
                  <option value="high">High (Better accuracy)</option>
                  <option value="ultra">Ultra (Maximum quality)</option>
                </select>
              </div>

              {/* Advanced Options Toggle */}
              <div className="mb-4">
                <button
                  className={`text-sm flex items-center transition-colors ${isDark ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-800'}`}
                  onClick={() => setShowAdvanced(!showAdvanced)}
                  type="button"
                >
                  <span>{showAdvanced ? 'Hide' : 'Show'} Advanced Options</span>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className={`h-4 w-4 ml-1 transition-transform ${showAdvanced ? 'transform rotate-180' : ''}`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
              </div>

              {/* Advanced Settings */}
              {showAdvanced && (
                <div className={`space-y-4 p-3 rounded-lg mb-4 ${isDark ? 'bg-gray-700' : 'bg-gray-50'}`}>
                  
                  {/* Content Options */}
                  <div className="space-y-3">
                    <h4 className={`font-medium text-sm ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>Content Options</h4>

                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="includeMetadata"
                        checked={includeMetadata}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setIncludeMetadata(e.target.checked)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor="includeMetadata" className={`ml-2 block text-sm ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                        Include document metadata
                      </label>
                    </div>

                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="includeStructure"
                        checked={includeStructure}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setIncludeStructure(e.target.checked)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor="includeStructure" className={`ml-2 block text-sm ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                        Preserve document structure
                      </label>
                    </div>

                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="includeAnnotations"
                        checked={includeAnnotations}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setIncludeAnnotations(e.target.checked)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor="includeAnnotations" className={`ml-2 block text-sm ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                        Include annotations and comments
                      </label>
                    </div>

                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="includeForms"
                        checked={includeForms}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setIncludeForms(e.target.checked)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor="includeForms" className={`ml-2 block text-sm ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                        Include form fields and data
                      </label>
                    </div>

                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="includeBookmarks"
                        checked={includeBookmarks}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setIncludeBookmarks(e.target.checked)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor="includeBookmarks" className={`ml-2 block text-sm ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                        Include bookmarks and navigation
                      </label>
                    </div>

                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="preserveFormatting"
                        checked={preserveFormatting}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPreserveFormatting(e.target.checked)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor="preserveFormatting" className={`ml-2 block text-sm ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                        Preserve text formatting (fonts, styles)
                      </label>
                    </div>

                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="cleanupText"
                        checked={cleanupText}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCleanupText(e.target.checked)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor="cleanupText" className={`ml-2 block text-sm ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                        Clean up extracted text
                      </label>
                    </div>

                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                        Text Extraction Mode
                      </label>
                      <select
                        className={`w-full p-2 border rounded-lg transition-colors ${isDark ? 'bg-gray-600 border-gray-500 text-white focus:ring-blue-500 focus:border-blue-500' : 'bg-white border-gray-300 text-gray-900 focus:ring-blue-500 focus:border-blue-500'}`}
                        value={textExtraction}
                        onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setTextExtraction(e.target.value as TextExtractionMode)}
                      >
                        <option value="layout-preserved">Layout Preserved</option>
                        <option value="reading-order">Reading Order</option>
                        <option value="raw-text">Raw Text</option>
                        <option value="structured-text">Structured Text</option>
                      </select>
                    </div>

                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                        Image Handling
                      </label>
                      <select
                        className={`w-full p-2 border rounded-lg transition-colors ${isDark ? 'bg-gray-600 border-gray-500 text-white focus:ring-blue-500 focus:border-blue-500' : 'bg-white border-gray-300 text-gray-900 focus:ring-blue-500 focus:border-blue-500'}`}
                        value={imageHandling}
                        onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setImageHandling(e.target.value as ImageHandling)}
                      >
                        <option value="ignore">Ignore images</option>
                        <option value="link">Link to images</option>
                        <option value="extract">Extract as files</option>
                        <option value="embed">Embed in XML</option>
                        <option value="base64">Base64 encoding</option>
                      </select>
                    </div>
                  </div>

                  {/* XML-Specific Options */}
                  {outputFormat === 'xml' && (
                    <div className="space-y-3">
                      <h4 className={`font-medium text-sm ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>XML Options</h4>

                      {xmlSchema === 'custom' && (
                        <>
                          <div>
                            <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                              Root Element Name
                            </label>
                            <input
                              type="text"
                              className={`w-full p-2 border rounded-lg transition-colors ${isDark ? 'bg-gray-600 border-gray-500 text-white focus:ring-blue-500 focus:border-blue-500' : 'bg-white border-gray-300 text-gray-900 focus:ring-blue-500 focus:border-blue-500'}`}
                              value={rootElement}
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setRootElement(e.target.value)}
                              placeholder="document"
                            />
                          </div>

                          <div>
                            <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                              Namespace URI (optional)
                            </label>
                            <input
                              type="text"
                              className={`w-full p-2 border rounded-lg transition-colors ${isDark ? 'bg-gray-600 border-gray-500 text-white focus:ring-blue-500 focus:border-blue-500' : 'bg-white border-gray-300 text-gray-900 focus:ring-blue-500 focus:border-blue-500'}`}
                              value={namespaceUri}
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNamespaceUri(e.target.value)}
                              placeholder="http://example.com/namespace"
                            />
                          </div>
                        </>
                      )}

                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id="indentation"
                          checked={indentation}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setIndentation(e.target.checked)}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <label htmlFor="indentation" className={`ml-2 block text-sm ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                          Pretty print with indentation
                        </label>
                      </div>

                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id="validateXML"
                          checked={validateXML}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setValidateXML(e.target.checked)}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <label htmlFor="validateXML" className={`ml-2 block text-sm ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                          Validate XML structure
                        </label>
                      </div>

                      <div>
                        <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                          Character Encoding
                        </label>
                        <select
                          className={`w-full p-2 border rounded-lg transition-colors ${isDark ? 'bg-gray-600 border-gray-500 text-white focus:ring-blue-500 focus:border-blue-500' : 'bg-white border-gray-300 text-gray-900 focus:ring-blue-500 focus:border-blue-500'}`}
                          value={encoding}
                          onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setEncoding(e.target.value)}
                        >
                          <option value="UTF-8">UTF-8 (Recommended)</option>
                          <option value="UTF-16">UTF-16</option>
                          <option value="ASCII">ASCII</option>
                          <option value="ISO-8859-1">ISO-8859-1</option>
                          <option value="Windows-1252">Windows-1252</option>
                        </select>
                      </div>
                    </div>
                  )}

                  {/* OCR Options */}
                  <div className="space-y-3">
                    <h4 className={`font-medium text-sm ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>OCR & Image Processing</h4>

                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="ocrEnabled"
                        checked={ocrEnabled}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setOcrEnabled(e.target.checked)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor="ocrEnabled" className={`ml-2 block text-sm ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                        Enable OCR for scanned documents
                      </label>
                    </div>

                    {ocrEnabled && (
                      <div>
                        <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                          OCR Language
                        </label>
                        <select
                          className={`w-full p-2 border rounded-lg transition-colors ${isDark ? 'bg-gray-600 border-gray-500 text-white focus:ring-blue-500 focus:border-blue-500' : 'bg-white border-gray-300 text-gray-900 focus:ring-blue-500 focus:border-blue-500'}`}
                          value={ocrLanguage}
                          onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setOcrLanguage(e.target.value)}
                        >
                          <option value="eng">English</option>
                          <option value="spa">Spanish</option>
                          <option value="fra">French</option>
                          <option value="deu">German</option>
                          <option value="ita">Italian</option>
                          <option value="por">Portuguese</option>
                          <option value="rus">Russian</option>
                          <option value="chi_sim">Chinese (Simplified)</option>
                          <option value="chi_tra">Chinese (Traditional)</option>
                          <option value="jpn">Japanese</option>
                          <option value="kor">Korean</option>
                          <option value="ara">Arabic</option>
                          <option value="hin">Hindi</option>
                        </select>
                      </div>
                    )}

                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="removeWatermarks"
                        checked={removeWatermarks}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setRemoveWatermarks(e.target.checked)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor="removeWatermarks" className={`ml-2 block text-sm ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                        Attempt to remove watermarks
                      </label>
                    </div>

                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                        Brightness: {brightness}%
                      </label>
                      <input
                        type="range"
                        min="50"
                        max="150"
                        value={brightness}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setBrightness(parseInt(e.target.value))}
                        className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                      />
                    </div>

                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                        Contrast: {contrast}%
                      </label>
                      <input
                        type="range"
                        min="50"
                        max="150"
                        value={contrast}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setContrast(parseInt(e.target.value))}
                        className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Convert Button */}
              <div className="mt-6">
                <button
                  className={`w-full px-4 py-3 rounded-lg font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                    selectedFile && !isConverting
                      ? isDark
                        ? 'bg-blue-600 hover:bg-blue-700 text-white focus:ring-blue-500'
                        : 'bg-blue-600 hover:bg-blue-700 text-white focus:ring-blue-500'
                      : isDark
                        ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
                  onClick={convertToXML}
                  disabled={!selectedFile || isConverting}
                  type="button"
                >
                  {isConverting ? (
                    <div className="flex items-center justify-center">
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Converting... {Math.round(conversionProgress)}%
                    </div>
                  ) : (
                    `Convert to ${outputFormat.toUpperCase()}`
                  )}
                </button>

                {isConverting && (
                  <div className="mt-2">
                    <div className={`w-full bg-gray-200 rounded-full h-2 ${isDark ? 'bg-gray-700' : 'bg-gray-200'}`}>
                      <div 
                        className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${conversionProgress}%` }}
                      ></div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Results Section */}
          <div className={`p-4 rounded-lg shadow-sm border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
            <h3 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Conversion Results
            </h3>

            {/* Status Messages */}
            {error && (
              <div className={`border p-3 rounded-lg mb-4 ${isDark ? 'bg-red-900/20 border-red-800 text-red-200' : 'bg-red-50 border-red-200 text-red-700'}`}>
                <p className="font-medium">Error</p>
                <p>{error}</p>
              </div>
            )}

            {success && (
              <div className={`border p-3 rounded-lg mb-4 ${isDark ? 'bg-green-900/20 border-green-800 text-green-200' : 'bg-green-50 border-green-200 text-green-700'}`}>
                <div className="flex items-center mb-2">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <p className="font-medium">Success!</p>
                </div>
                <p>{success}</p>
              </div>
            )}

            {!selectedFile && !error && !success && (
              <div className={`border p-8 rounded-lg flex items-center justify-center ${isDark ? 'bg-gray-700 border-gray-600 text-gray-400' : 'bg-gray-50 border-gray-200 text-gray-500'}`}>
                <div className="text-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <p>
                    Upload a PDF file to start conversion
                  </p>
                </div>
              </div>
            )}

            {selectedFile && !error && !success && !isConverting && (
              <div className={`border p-6 rounded-lg ${isDark ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
                <div className="text-center">
                  <div className={`text-4xl mb-3 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    📝
                  </div>
                  <p className={`text-lg font-medium mb-2 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                    Ready to Convert
                  </p>
                  <p className={`${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    Click the convert button to transform your PDF into structured {outputFormat.toUpperCase()} format
                  </p>
                </div>
              </div>
            )}

            {/* Conversion Tips */}
            <div className={`mt-6 p-4 rounded-lg ${isDark ? 'bg-gray-700' : 'bg-gray-50'}`}>
              <h4 className={`font-medium mb-2 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>💡 Conversion Tips</h4>
              <ul className={`text-sm space-y-1 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                <li>• Choose appropriate schema for your use case (TEI for academic texts, Dublin Core for metadata)</li>
                <li>• Enable OCR for scanned documents or image-based PDFs</li>
                <li>• Use structure preservation to maintain document hierarchy</li>
                <li>• Include metadata for comprehensive document information</li>
                <li>• Test with a small page range first for large documents</li>
              </ul>
            </div>

            {/* Quick Actions */}
            {selectedFile && (
              <div className="mt-4 flex space-x-2">
                <button
                  className={`flex-1 px-3 py-2 rounded-lg font-medium transition-colors ${isDark ? 'bg-gray-700 hover:bg-gray-600 text-gray-200' : 'bg-gray-200 hover:bg-gray-300 text-gray-800'}`}
                  onClick={clearAll}
                  type="button"
                >
                  🗑️ Clear All
                </button>
                <button
                  className={`flex-1 px-3 py-2 rounded-lg font-medium transition-colors ${isDark ? 'bg-gray-700 hover:bg-gray-600 text-gray-200' : 'bg-gray-200 hover:bg-gray-300 text-gray-800'}`}
                  onClick={() => {
                    setOutputFormat('xml');
                    setExtractionMode('full-document');
                    setXmlSchema('custom');
                    setQualityLevel('balanced');
                    track('settings_reset');
                  }}
                  type="button"
                >
                  🔄 Reset Settings
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Information Section */}
        <div className={`mt-8 p-6 rounded-lg shadow-sm border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
          <h2 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>About PDF to XML Conversion</h2>

          <div className="space-y-4">
            <div>
              <h3 className={`font-medium mb-2 ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>How This Converter Works</h3>
              <p className={`${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                This advanced PDF to XML converter analyzes PDF document structure, extracts content, metadata, and formatting information,
                then converts it into well-structured XML format. The tool supports multiple XML schemas including TEI, Dublin Core, MODS,
                and custom schemas, making it perfect for digital humanities, data processing, and content management applications.
              </p>
            </div>

            <div>
              <h3 className={`font-medium mb-2 ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>Supported XML Schemas</h3>
              <ul className={`list-disc pl-5 space-y-1 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                <li><strong>Custom Schema</strong> - User-defined XML structure with configurable root elements and namespaces</li>
                <li><strong>TEI (Text Encoding Initiative)</strong> - Standard for encoding literary and linguistic texts</li>
                <li><strong>Dublin Core</strong> - Metadata schema for describing digital resources</li>
                <li><strong>MODS (Metadata Object Description Schema)</strong> - Rich bibliographic metadata format</li>
                <li><strong>ALTO (Analyzed Layout and Text Object)</strong> - Technical metadata for layout and text recognition</li>
                <li><strong>PAGE XML</strong> - Format for page content representation and OCR output</li>
              </ul>
            </div>

            <div>
              <h3 className={`font-medium mb-2 ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>Extraction Modes</h3>
              <ul className={`list-disc pl-5 space-y-1 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                <li><strong>Full Document</strong> - Complete extraction including text, structure, metadata, and images</li>
                <li><strong>Text Only</strong> - Extract only textual content without formatting or structure</li>
                <li><strong>Structure Only</strong> - Extract document hierarchy, headings, and organizational elements</li>
                <li><strong>Tables Only</strong> - Focus on extracting tabular data and table structures</li>
                <li><strong>Images Only</strong> - Extract and catalog images, graphics, and visual elements</li>
                <li><strong>Metadata Only</strong> - Extract document properties, author information, and technical metadata</li>
              </ul>
            </div>

            <div>
              <h3 className={`font-medium mb-2 ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>Output Format Options</h3>
              <ul className={`list-disc pl-5 space-y-1 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                <li><strong>XML (.xml)</strong> - Structured markup format with schema validation and namespace support</li>
                <li><strong>JSON (.json)</strong> - JavaScript Object Notation for web applications and APIs</li>
                <li><strong>HTML (.html)</strong> - Web-ready format with preserved structure and styling</li>
                <li><strong>Plain Text (.txt)</strong> - Simple text format with optional structure markers</li>
                <li><strong>CSV (.csv)</strong> - Comma-separated format for tabular data extraction</li>
              </ul>
            </div>

            <div>
              <h3 className={`font-medium mb-2 ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>Advanced Features</h3>
              <ul className={`list-disc pl-5 space-y-1 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                <li><strong>OCR Integration</strong> - Extract text from scanned PDFs and images in 13+ languages</li>
                <li><strong>Structure Preservation</strong> - Maintain document hierarchy, headings, and logical organization</li>
                <li><strong>Metadata Extraction</strong> - Comprehensive document properties, creation info, and technical details</li>
                <li><strong>Form Data Processing</strong> - Extract form fields, values, and interactive elements</li>
                <li><strong>Annotation Support</strong> - Include comments, highlights, and markup information</li>
                <li><strong>Bookmark Navigation</strong> - Preserve document navigation structure and links</li>
                <li><strong>Image Processing</strong> - Multiple handling options including embedding, linking, and extraction</li>
                <li><strong>Schema Validation</strong> - Ensure output compliance with selected XML schemas</li>
                <li><strong>Custom Namespaces</strong> - Support for user-defined XML namespaces and vocabularies</li>
                <li><strong>Text Enhancement</strong> - Cleanup, formatting preservation, and reading order optimization</li>
              </ul>
            </div>

            <div>
              <h3 className={`font-medium mb-2 ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>Use Cases</h3>
              <ul className={`list-disc pl-5 space-y-1 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                <li><strong>Digital Humanities</strong> - Convert historical documents and literary texts to TEI XML for analysis</li>
                <li><strong>Content Management</strong> - Transform documents for CMS integration and web publishing</li>
                <li><strong>Data Processing</strong> - Extract structured data from reports, forms, and business documents</li>
                <li><strong>Academic Research</strong> - Process research papers, theses, and scholarly publications</li>
                <li><strong>Library Sciences</strong> - Catalog and digitize archival materials with proper metadata</li>
                <li><strong>Legal Documents</strong> - Structure contracts, court documents, and legal briefs</li>
                <li><strong>Publishing Workflow</strong> - Convert manuscripts and publications to XML for multi-format output</li>
                <li><strong>Government Records</strong> - Process official documents and public records for digital archives</li>
              </ul>
            </div>

            <div>
              <h3 className={`font-medium mb-2 ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>Best Practices</h3>
              <ul className={`list-disc pl-5 space-y-1 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                <li>Choose the appropriate schema based on your specific use case and target application</li>
                <li>Enable structure preservation for documents with clear hierarchical organization</li>
                <li>Use OCR for scanned documents or when text appears as images</li>
                <li>Include metadata extraction for comprehensive document information</li>
                <li>Test conversion settings with a small page range before processing large documents</li>
                <li>Validate XML output if compliance with specific schemas is required</li>
                <li>Consider image handling options based on your storage and bandwidth requirements</li>
                <li>Use appropriate quality levels balancing processing time with accuracy needs</li>
              </ul>
            </div>

            <div>
              <h3 className={`font-medium mb-2 ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>Technical Specifications</h3>
              <ul className={`list-disc pl-5 space-y-1 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                <li><strong>Input:</strong> PDF files up to 100MB in size</li>
                <li><strong>Output:</strong> XML, JSON, HTML, TXT, and CSV formats</li>
                <li><strong>Processing:</strong> Client-side conversion for privacy and security</li>
                <li><strong>Schemas:</strong> TEI, Dublin Core, MODS, ALTO, PAGE XML, and custom schemas</li>
                <li><strong>OCR:</strong> 13 language support with Tesseract engine</li>
                <li><strong>Encoding:</strong> UTF-8, UTF-16, ASCII, and other character encodings</li>
                <li><strong>Validation:</strong> Built-in XML schema validation and well-formedness checking</li>
                <li><strong>Performance:</strong> Multi-threaded processing with progress tracking</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Wrapper component with ThemeProvider
const PDFToXML: React.FC = () => {
  return (
    <ThemeProvider>
      <PDFToXMLContent />
    </ThemeProvider>
  );
};

export default PDFToXML;